/* eslint-disable react/display-name */
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState, forwardRef } from 'react';
import dynamic from 'next/dynamic';
import styled, { css } from 'styled-components';
import { components } from 'react-select';
import { Form } from 'react-bootstrap';

const LibSelect = dynamic(() => import('react-select'), {
  ssr: false,
});

import { theme } from '_constants/theme';
import { ICONS_ALIASES } from '_constants/icons';
import { hexToRGBA } from '_helpers/theme';

import Icon from '_components/atomic/Icon';

const { colors, devices, fontSizes, spaces } = theme;

const StyledGroup = styled(
  forwardRef(({ labeled, bottomSpace, ...props }, ref) => (
    <Form.Group ref={ref} {...props} />
  ))
)`
  width: 100%;
  padding-top: ${({ labeled }) => (labeled ? spaces.largeX : 0)}px;

  ${({ bottomSpace }) =>
    bottomSpace &&
    css`
      margin-bottom: ${bottomSpace}px;
    `}
`;

const StyledSelectContainer = styled(
  forwardRef(({ invalid, open, ...props }, ref) => <div ref={ref} {...props} />)
)`
  min-height: 56px;

  padding-top: ${spaces.small}px;
  padding-bottom: ${spaces.small}px;
  padding-left: ${spaces.regular}px;
  padding-right: ${spaces.regular}px;

  border: 1px solid ${colors.indigo[600]};
  border-radius: 28px;
  background-color: ${colors.white};

  .badge-select-container {
    .badge-select__control {
      min-height: unset;
      border: none;
      background: none;
      cursor: pointer;

      .badge-select__indicator-separator {
        display: none;
      }

      .badge-select__indicator {
        &.badge-select__dropdown-indicator {
          pointer-events: none;
        }
      }

      .badge-select__indicator {
        padding: 0;
      }

      .badge-select__value-container {
        height: 30px;
        position: relative;
        padding-left: 86px;
        padding-right: 0;
        padding-top: 0;
        padding-bottom: 0;
        overflow: visible;

        .badge-select__input-container {
          padding: 0;
          margin: 0;

          font-family: 'Manrope';
          font-size: ${fontSizes.medium}px;
          font-style: normal;
          font-weight: 600;
          line-height: ${`calc(${fontSizes.medium}px * 1.5)`};
          color: ${colors.gray[700]};
        }

        .badge-select__single-value {
          margin: 0;
          font-family: 'Manrope';
          font-size: ${fontSizes.medium}px;
          font-style: normal;
          font-weight: 600;
          line-height: ${`calc(${fontSizes.medium}px * 1.5)`};
          color: ${colors.gray[700]};
        }

        .badge-select__placeholder {
          margin: 0;
          font-family: 'Manrope';
          font-size: ${fontSizes.medium}px;
          font-style: normal;
          font-weight: 600;
          line-height: ${`calc(${fontSizes.medium}px * 1.5)`};
          color: ${colors.gray[500]};
        }
      }

      &.badge-select__control--is-focused {
        box-shadow: none;
        border-color: ${colors.indigo[600]};
      }
    }

    .badge-select__menu {
      min-width: 200px;
      right: 0;
      background-color: ${colors.white};
      z-index: 9999;

      .badge-select__menu-list {
        .badge-select__option {
          font-family: 'Manrope';
          font-size: ${fontSizes.medium}px;
          font-style: normal;
          font-weight: 600;
          line-height: ${`calc(${fontSizes.medium}px * 1.5)`};
          color: ${colors.indigo[600]};

          cursor: pointer;

          &.badge-select__option--is-focused {
            background-color: ${colors.indigo[600]};
            color: ${colors.white};
          }

          &.badge-select__option--is-selected {
            background-color: ${colors.indigo[300]};
            color: ${colors.white};
          }
        }
      }
    }
  }

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${colors.gray[200]};
    `}

  ${({ open }) =>
    open &&
    css`
      border-color: ${colors.indigo[600]};
    `}

  ${({ invalid, open }) =>
    invalid &&
    !open &&
    css`
      border-color: ${colors.orange[600]};
    `}
`;

const StyledLabel = styled(
  forwardRef(({ invalid, ...props }, ref) => <div ref={ref} {...props} />)
)`
  margin-left: ${spaces.smallest}px;
  margin-bottom: ${spaces.small2X}px;

  font-family: 'Manrope';
  font-size: ${fontSizes.small}px;
  font-style: normal;
  font-weight: 600;
  line-height: ${`calc(${fontSizes.small}px * 1.5)`};
  color: ${colors.gray[700]};

  span {
    color: ${colors.orange[600]};
  }

  ${({ invalid }) =>
    invalid &&
    css`
      color: ${colors.orange[600]};
    `}
`;

const StyledIconContainer = styled(
  forwardRef(({ desktop, mobile, ...props }, ref) => (
    <div ref={ref} {...props} />
  ))
)`
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ mobile }) =>
    mobile &&
    css`
      display: none;
    `}

  @media ${devices.tablet} {
    ${({ desktop }) =>
      desktop &&
      css`
        display: none;
      `}

    ${({ mobile }) =>
      mobile &&
      css`
        display: flex;
      `}
  }
`;

const StyledBadge = styled.div`
  display: inline-flex;
  align-items: center;
  position: absolute;
  min-width: 88px;
  top: -13px;
  bottom: -13px;
  left: -17px;
  padding-top: 12px;
  padding-bottom: 12px;
  padding-left: 20px;
  padding-right: 20px;
  z-index: 0;
  border: 1px solid ${theme.colors.indigo[600]};
  border-top-left-radius: 28px;
  border-bottom-left-radius: 28px;
  background-color: ${hexToRGBA(theme.colors.indigo[600], 0.2)};
  color: ${theme.colors.indigo[600]};
`;

const StyledFeedback = styled(Form.Control.Feedback)`
  padding-top: ${theme.spaces.small2X}px;
  font-family: 'Manrope';
  font-size: ${fontSizes.small}px;
  font-style: normal;
  font-weight: 400;
  line-height: ${`calc(${fontSizes.small}px * 1.5)`};
  color: ${colors.placeholder};

  &.processing-feedback {
    color: ${colors.gray[500]};
  }

  &.valid-feedback {
    color: ${colors.indigo[600]};
  }

  &.invalid-feedback {
    color: ${colors.orange[600]};
  }
`;

const BadgeSelect = ({
  name,
  label,
  labelSpace,
  badgeLabel = 'Label',
  placeholder,
  bottomSpace,
  clearable,
  searchable,
  disabled,
  required,
  valid,
  invalid,
  feedback,
  options = [],
  value,
  onChange,
  onBlur,
  ...restProps
}) => {
  const [open, setOpen] = useState(false);
  const [innerOptions, setInnerOptions] = useState([]);

  const currentValue = useMemo(
    () => innerOptions?.find((option) => option?.value === value),
    [value, innerOptions]
  );

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = useCallback(() => {
    setOpen(false);
    onBlur && onBlur({ target: { name } });
  }, [name, onBlur]);

  const handleChange = useCallback(
    (option) => {
      onChange && onChange({ target: { name, value: option?.value || '' } });
    },
    [onChange, name]
  );

  useEffect(() => {
    setInnerOptions(
      options.map(({ value, title }) => {
        return { value, label: title };
      })
    );
  }, [options]);

  const DropdownIndicator = (props) => {
    return (
      <components.DropdownIndicator {...props}>
        {valid && (
          <StyledIconContainer>
            <Icon alias={ICONS_ALIASES.check} color={colors.indigo[500]} />
          </StyledIconContainer>
        )}
        {invalid && (
          <StyledIconContainer>
            <Icon alias={ICONS_ALIASES.close} color={colors.orange[600]} />
          </StyledIconContainer>
        )}
        {!valid && !invalid && open && (
          <StyledIconContainer>
            <Icon alias={ICONS_ALIASES.chevronUp} color={colors.gray[700]} />
          </StyledIconContainer>
        )}
        {!valid && !invalid && !open && (
          <StyledIconContainer>
            <Icon alias={ICONS_ALIASES.chevronDown} color={colors.gray[700]} />
          </StyledIconContainer>
        )}
      </components.DropdownIndicator>
    );
  };

  const ValueContainer = useMemo(
    () =>
      ({ children, ...props }) => {
        return (
          <components.ValueContainer {...props}>
            {!!children && <StyledBadge>{badgeLabel}</StyledBadge>}
            {children}
          </components.ValueContainer>
        );
      },
    [badgeLabel]
  );

  return (
    <StyledGroup
      labeled={labelSpace}
      bottomSpace={bottomSpace}
      className={invalid ? 'custom-control-invalid' : ''}
    >
      {label && (
        <StyledLabel invalid={invalid}>
          {label}
          {label && required && <span>&nbsp;*</span>}
        </StyledLabel>
      )}
      <StyledSelectContainer disabled={disabled} invalid={invalid} open={open}>
        <LibSelect
          className="badge-select-container"
          classNamePrefix="badge-select"
          components={{ DropdownIndicator, ValueContainer }}
          isClearable={clearable}
          isDisabled={disabled}
          isSearchable={searchable}
          options={innerOptions}
          placeholder={placeholder || 'Select state...'}
          value={currentValue}
          onChange={handleChange}
          onMenuOpen={handleOpen}
          onMenuClose={handleClose}
        />
      </StyledSelectContainer>
      <Form.Control
        readOnly
        hidden
        name={name}
        isValid={valid}
        isInvalid={invalid}
        {...restProps}
      />
      {!valid && !invalid && feedback && (
        <StyledFeedback type="processing">{feedback}</StyledFeedback>
      )}
      {feedback && <StyledFeedback type="valid">{feedback}</StyledFeedback>}
      {feedback && <StyledFeedback type="invalid">{feedback}</StyledFeedback>}
    </StyledGroup>
  );
};

BadgeSelect.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  labelSpace: PropTypes.bool,
  badgeLabel: PropTypes.string,
  placeholder: PropTypes.string,
  bottomSpace: PropTypes.number,
  clearable: PropTypes.bool,
  disabled: PropTypes.bool,
  searchable: PropTypes.bool,
  required: PropTypes.bool,
  valid: PropTypes.bool,
  invalid: PropTypes.bool,
  feedback: PropTypes.node,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
};

export default BadgeSelect;
